import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import {
  LockOutlined,
  BlockOutlined,
  WarningAmberOutlined,
  DnsOutlined,
  ErrorOutlineOutlined,
  ArrowBackOutlined,
  MailOutlined,
} from '@mui/icons-material';
import LogoIcon from '../components/LogoIcon';
import { useThemeMode } from '../contexts/ThemeContext';

type ReasonKey = 'unauthorized' | 'not_invited' | 'deactivated' | 'domain' | 'ms_error' | 'error';

interface ReasonData {
  httpCode: string;
  statusCode: '401' | '403';
  heading: string;
  body: string;
  Icon: React.ElementType;
}

const REASON_MAP: Record<string, ReasonData> = {
  unauthorized: {
    statusCode: '401',
    httpCode: 'HTTP 401 · UNAUTHORIZED',
    heading: 'Authentication required',
    body: "Your session has expired or you haven't signed in yet. Please sign in to continue.",
    Icon: WarningAmberOutlined,
  },
  not_invited: {
    statusCode: '403',
    httpCode: 'HTTP 403 · FORBIDDEN',
    heading: "You haven't been invited",
    body: "Your account doesn't have access to this platform. Contact your administrator to request an invitation.",
    Icon: LockOutlined,
  },
  deactivated: {
    statusCode: '403',
    httpCode: 'HTTP 403 · ACCOUNT_DISABLED',
    heading: 'Account deactivated',
    body: 'Your access has been suspended by an administrator. Contact your administrator to restore access.',
    Icon: BlockOutlined,
  },
  domain: {
    statusCode: '403',
    httpCode: 'HTTP 403 · DOMAIN_RESTRICTED',
    heading: 'Unauthorized domain',
    body: 'This platform is restricted to TestingXperts organization accounts. Sign in with your @testingxperts.com Microsoft account.',
    Icon: DnsOutlined,
  },
  ms_error: {
    statusCode: '401',
    httpCode: 'HTTP 401 · SSO_ERROR',
    heading: 'Sign-in blocked by Microsoft',
    body: 'Microsoft Azure AD blocked the sign-in. Your account may not have been granted app access. Contact your IT administrator.',
    Icon: ErrorOutlineOutlined,
  },
  error: {
    statusCode: '401',
    httpCode: 'HTTP 401 · AUTH_FAILED',
    heading: 'Authentication failed',
    body: 'Something went wrong during sign-in. Please try again. If the problem persists, contact your administrator.',
    Icon: ErrorOutlineOutlined,
  },
};

const AccessDeniedPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const reasonKey = (searchParams.get('reason') ?? 'not_invited') as ReasonKey;
  const content = REASON_MAP[reasonKey] ?? REASON_MAP['not_invited'];
  const { Icon } = content;

  const is401  = content.statusCode === '401';
  const accent = is401 ? '#F59E0B' : '#FF1A50';

  const bg     = isDark ? '#0A0A0A' : '#F5F5F5';
  const card   = isDark ? '#111111' : '#FFFFFF';
  const border = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const textP  = isDark ? '#EFEFEF' : '#0A0A0A';
  const textS  = isDark ? '#888888' : '#666666';
  const textM  = isDark ? '#555555' : '#AAAAAA';

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: bg,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      p: 3,
    }}>
      <Box sx={{ width: '100%', maxWidth: 400 }}>

        {/* Brand */}
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 8, cursor: 'pointer' }}
          onClick={() => navigate('/login')}
        >
          <LogoIcon size={20} letterColor={isDark ? '#EFEFEF' : '#FF1A50'} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: textP, letterSpacing: '-0.02em' }}>
            LLM Wrapper
          </Typography>
          <Box sx={{
            ml: 0.5, px: 1, py: 0.25, borderRadius: '4px',
            bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
            border: `1px solid ${border}`,
          }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 500, color: textM, letterSpacing: '0.04em' }}>
              ADMIN
            </Typography>
          </Box>
        </Box>

        {/* Error code */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: accent, flexShrink: 0 }} />
          <Typography sx={{
            fontFamily: '"JetBrains Mono", "Courier New", monospace',
            fontSize: '0.7rem', fontWeight: 600,
            color: accent, letterSpacing: '0.06em',
          }}>
            {content.httpCode}
          </Typography>
        </Box>

        {/* Heading */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box sx={{
            width: 28, height: 28, borderRadius: '6px', flexShrink: 0,
            bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
            border: `1px solid ${border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: accent,
          }}>
            <Icon sx={{ fontSize: 15 }} />
          </Box>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: textP, letterSpacing: '-0.03em', lineHeight: 1.2 }}>
            {content.heading}
          </Typography>
        </Box>

        <Typography sx={{ fontSize: '0.875rem', color: textS, mb: 5, lineHeight: 1.65, pl: '44px' }}>
          {content.body}
        </Typography>

        {/* Card with actions */}
        <Box sx={{
          bgcolor: card,
          border: `1px solid ${border}`,
          borderRadius: '10px',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}>
          <Button
            fullWidth
            startIcon={<ArrowBackOutlined sx={{ fontSize: 15 }} />}
            onClick={() => navigate('/login')}
            sx={{
              py: 1.25,
              fontSize: '0.875rem',
              fontWeight: 500,
              bgcolor: isDark ? '#EFEFEF' : '#0A0A0A',
              color: isDark ? '#0A0A0A' : '#EFEFEF',
              borderRadius: '7px',
              border: 'none',
              letterSpacing: '-0.01em',
              '&:hover': { bgcolor: isDark ? '#FFFFFF' : '#1A1A1A' },
              transition: 'background 0.15s ease',
              textTransform: 'none',
            }}
          >
            Back to Sign In
          </Button>

          {reasonKey !== 'domain' && (
            <Button
              fullWidth
              startIcon={<MailOutlined sx={{ fontSize: 15 }} />}
              component="a"
              href="mailto:it@testingxperts.com?subject=LLM%20Wrapper%20Access%20Request"
              sx={{
                py: 1.1,
                fontSize: '0.8rem',
                fontWeight: 500,
                color: textS,
                borderRadius: '7px',
                border: `1px solid ${border}`,
                bgcolor: 'transparent',
                '&:hover': {
                  bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                  color: textP,
                },
                transition: 'all 0.15s ease',
                textTransform: 'none',
              }}
            >
              Request access from IT
            </Button>
          )}
        </Box>

        {/* Footer */}
        <Typography sx={{ mt: 4, fontSize: '0.72rem', color: textM, textAlign: 'center', lineHeight: 1.6 }}>
          Access restricted to authorized TestingXperts personnel.
        </Typography>
      </Box>
    </Box>
  );
};

export default AccessDeniedPage;
